import { fromBase64Url, fromUtf8, randomBytes, toBase64Url, toHex, utf8 } from "./encoding";

/** 128 bits: unguessable, and short enough for a link fragment (22 chars). */
const SECRET_BYTES = 16;
const IV_BYTES = 12;
const HKDF_SALT = utf8("codeshare");

/** Distinct HKDF `info` labels so each derived value is independent. Changing these breaks rooms. */
const INFO = {
  topic: "codeshare/v1/topic",
  signaling: "codeshare/v1/signaling",
  auth: "codeshare/v1/auth",
} as const;

export interface RoomKeys {
  /** Public rendezvous id given to signaling relays (hex). Reveals nothing about the secret. */
  topic: string;
  /** Encrypts signaling payloads end to end (AES-GCM). */
  sigKey: CryptoKey;
  /** Proves room membership to peers (HMAC-SHA256). */
  authKey: CryptoKey;
}

export function newRoomSecret(): string {
  return toBase64Url(randomBytes(SECRET_BYTES));
}

export function isRoomSecret(value: string): boolean {
  try {
    return fromBase64Url(value).length === SECRET_BYTES;
  } catch {
    return false;
  }
}

export async function deriveRoomKeys(secret: string): Promise<RoomKeys> {
  if (!isRoomSecret(secret)) throw new Error("invalid room secret");
  const material = await crypto.subtle.importKey("raw", fromBase64Url(secret), "HKDF", false, [
    "deriveBits",
    "deriveKey",
  ]);
  const hkdf = (info: string) => ({
    name: "HKDF",
    hash: "SHA-256",
    salt: HKDF_SALT,
    info: utf8(info),
  });

  const [topicBits, sigKey, authKey] = await Promise.all([
    crypto.subtle.deriveBits(hkdf(INFO.topic), material, 256),
    crypto.subtle.deriveKey(
      hkdf(INFO.signaling),
      material,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    ),
    crypto.subtle.deriveKey(
      hkdf(INFO.auth),
      material,
      { name: "HMAC", hash: "SHA-256", length: 256 },
      false,
      ["sign", "verify"],
    ),
  ]);
  return { topic: toHex(new Uint8Array(topicBits)), sigKey, authKey };
}

/** Encrypts with AES-GCM: base64url(iv ‖ ciphertext+tag). */
export async function seal(key: CryptoKey, plaintext: string): Promise<string> {
  const iv = randomBytes(IV_BYTES);
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, utf8(plaintext));
  const sealed = new Uint8Array(IV_BYTES + ciphertext.byteLength);
  sealed.set(iv);
  sealed.set(new Uint8Array(ciphertext), IV_BYTES);
  return toBase64Url(sealed);
}

/** Decrypts `seal` output. Throws if the data was tampered with or the key is wrong. */
export async function open(key: CryptoKey, sealed: string): Promise<string> {
  const bytes = fromBase64Url(sealed);
  if (bytes.length <= IV_BYTES) throw new Error("sealed payload too short");
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: bytes.subarray(0, IV_BYTES) },
    key,
    bytes.subarray(IV_BYTES),
  );
  return fromUtf8(new Uint8Array(plaintext));
}

export function newNonce(): string {
  return toBase64Url(randomBytes(16));
}

/**
 * Binds room membership to one specific WebRTC connection: the prover signs both DTLS
 * fingerprints and the verifier's fresh nonce, so a relay can't replay or man-in-the-middle it.
 */
function proofMessage(
  proverFingerprint: string,
  verifierFingerprint: string,
  verifierNonce: string,
) {
  return utf8(`cs-auth-v1\n${proverFingerprint}\n${verifierFingerprint}\n${verifierNonce}`);
}

export async function authProof(
  authKey: CryptoKey,
  proverFingerprint: string,
  verifierFingerprint: string,
  verifierNonce: string,
): Promise<string> {
  const message = proofMessage(proverFingerprint, verifierFingerprint, verifierNonce);
  return toBase64Url(new Uint8Array(await crypto.subtle.sign("HMAC", authKey, message)));
}

/** Constant-time check (crypto.subtle.verify) of a peer's `authProof`. */
export async function verifyAuthProof(
  authKey: CryptoKey,
  proof: string,
  proverFingerprint: string,
  verifierFingerprint: string,
  verifierNonce: string,
): Promise<boolean> {
  let signature: Uint8Array<ArrayBuffer>;
  try {
    signature = fromBase64Url(proof);
  } catch {
    return false;
  }
  const message = proofMessage(proverFingerprint, verifierFingerprint, verifierNonce);
  return crypto.subtle.verify("HMAC", authKey, signature, message);
}

/**
 * The DTLS certificate fingerprint from an SDP ("sha-256 AB:CD:…"), normalized to lowercase
 * algorithm and uppercase hex. Undefined if the SDP has none.
 */
export function fingerprintFromSdp(sdp: string): string | undefined {
  const match = /^a=fingerprint:(\S+) ([0-9A-Fa-f:]+)\s*$/m.exec(sdp);
  if (!match?.[1] || !match[2]) return undefined;
  return `${match[1].toLowerCase()} ${match[2].toUpperCase()}`;
}
